import * as THREE from 'three';

// ============================================================
// Speed Lines Overlay Effect
//
// A fullscreen quad with a custom shader that draws radial lines
// emanating from the screen center. Intensity scales with kart speed.
// During boost the lines take on a warm orange tint and get stronger.
// ============================================================

const vertexShader = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

const fragmentShader = /* glsl */ `
  uniform float intensity;
  uniform float boostMix;
  uniform float time;
  varying vec2 vUv;

  void main() {
    vec2 center = vUv - 0.5;
    float dist = length(center);
    float angle = atan(center.y, center.x);

    // Radial speed lines -- two layers at different frequencies for depth
    float lines1 = abs(sin(angle * 40.0 + time * 2.0));
    lines1 = smoothstep(0.88, 1.0, lines1);

    float lines2 = abs(sin(angle * 80.0 - time * 3.5));
    lines2 = smoothstep(0.92, 1.0, lines2);

    float lines = max(lines1, lines2 * 0.6);

    // Radial falloff -- stronger at edges, fade toward center
    float radialFade = smoothstep(0.08, 0.5, dist);

    // Extra brightness near screen edges
    float edgeGlow = smoothstep(0.3, 0.7, dist) * 0.5;

    float alpha = (lines * radialFade + edgeGlow * 0.15) * intensity * 0.45;

    // Color: white by default, warm orange during boost
    vec3 baseColor = vec3(1.0, 0.95, 0.9);
    vec3 boostColor = vec3(1.0, 0.65, 0.25);
    vec3 color = mix(baseColor, boostColor, boostMix);

    gl_FragColor = vec4(color, alpha);
  }
`;

export class SpeedEffect {
  private mesh: THREE.Mesh;
  private material: THREE.ShaderMaterial;

  /** Current visual intensity (smoothed for transitions). */
  private currentIntensity = 0;
  /** Current boost colour mix (smoothed). */
  private currentBoostMix = 0;
  /** Accumulated time sent to the shader. */
  private elapsed = 0;

  constructor(camera: THREE.PerspectiveCamera) {
    this.material = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        intensity: { value: 0 },
        boostMix: { value: 0 },
        time: { value: 0 },
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthTest: false,
      depthWrite: false,
    });

    // Fullscreen triangle / quad — use a plane that covers NDC [-1,1]
    const geometry = new THREE.PlaneGeometry(2, 2);
    this.mesh = new THREE.Mesh(geometry, this.material);

    // Prevent the mesh from being affected by scene transforms.
    // Render it last, on top of everything else.
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 9999;

    // Attach to camera so it's always in front
    camera.add(this.mesh);
    // Position just inside the near plane
    this.mesh.position.set(0, 0, -0.11);
  }

  /**
   * Call every frame with the current kart speed info.
   *
   * @param speed      Current kart speed (world units / s)
   * @param maxSpeed   The MAX_SPEED constant
   * @param isBoosting Whether the boost timer is active
   * @param dt         Frame delta in seconds
   */
  update(speed: number, maxSpeed: number, isBoosting: boolean, dt: number): void {
    this.elapsed += dt;

    // Speed ratio [0, 1] — we only start showing lines above 60 %
    const ratio = Math.abs(speed) / maxSpeed;
    const threshold = 0.6;

    let targetIntensity = 0;
    if (ratio > threshold) {
      // Map [threshold, 1] -> [0, 1]
      targetIntensity = (ratio - threshold) / (1 - threshold);
    }

    // During boost, push intensity higher
    if (isBoosting) {
      targetIntensity = Math.min(targetIntensity + 0.5, 1.5);
    }

    // Smooth toward target (fast ramp-up, slower decay)
    const rampSpeed = targetIntensity > this.currentIntensity ? 8 : 4;
    this.currentIntensity += (targetIntensity - this.currentIntensity) * (1 - Math.exp(-rampSpeed * dt));

    // Boost colour mix
    const targetBoostMix = isBoosting ? 1 : 0;
    this.currentBoostMix += (targetBoostMix - this.currentBoostMix) * (1 - Math.exp(-6 * dt));

    // Push values to the shader
    this.material.uniforms.intensity.value = this.currentIntensity;
    this.material.uniforms.boostMix.value = this.currentBoostMix;
    this.material.uniforms.time.value = this.elapsed;

    // Hide mesh entirely when there is nothing to show (avoids overdraw)
    this.mesh.visible = this.currentIntensity > 0.01;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.material.dispose();
    // Remove from parent (camera)
    if (this.mesh.parent) {
      this.mesh.parent.remove(this.mesh);
    }
  }
}
